import {
  ChatTile,
  ContactUsTile,
  Query,
  TileType,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useHelpAndSupport from 'features/myVaro/hooks/useHelpAndSupport';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Linking,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetContactUsPageTiles from 'services/graphql/queries/GetContactUsPageTiles';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import ListItemGroup from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { SystemBanner } from 'shared/components/vxl/SystemBanner/SystemBanner';
import { SystemBannerType } from 'shared/components/vxl/SystemBanner/SystemBanner.types';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isWeb } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import useVaroUserFlow from 'shared/utils/useVaroUserFlow/useVaroUserFlow';
import { AnyVariables, useQuery } from 'urql';

const Help = () => {
  const { t: translatedText } = useTranslation('myVaro', { keyPrefix: 'help' });
  const router = useRouter();
  const flow = useVaroUserFlow();

  const { bottom } = useSafeAreaInsets();

  const { defaultTitle } = useDefaultErrorStrings();

  const { segmentClient } = useAnalyticsConfiguration();

  const {
    formattedPopularTopicsListData,
    warningMessage,
    subTitleUrl,
    subTitle,
    loadingHelpAndSupportData,
  } = useHelpAndSupport();

  useEffect(() => {
    if (warningMessage) {
      void segmentClient.logEvent('Alert Banner Shown', {
        name: 'Alert Banner Shown',
        category: 'servicing',
        source_name: 'help_and_support',
        subcategory: 'chat',
        alert_name: 'technical_issues',
      });
    }
  }, [warningMessage, segmentClient]);

  const [{ data, fetching: loadingContactUsTileData, error }, refetch] =
    useQuery<Query, AnyVariables>({
      query: GetContactUsPageTiles,
      variables: {
        input: { tileType: [TileType.ChatTile, TileType.ContactUsTile] },
      },
    });

  const contactUsTileInfo = data?.contactUsPageTile?.tiles?.find(
    (item): item is ContactUsTile => item.tileType === TileType.ContactUsTile,
  );

  const chatTileInfo = data?.contactUsPageTile?.tiles?.find(
    (item): item is ChatTile => item.tileType === TileType.ChatTile,
  );
  const handleSectionDividerPressAnalytics = usePressableAnalytics({
    analyticsId: 'allTopics',
    customAttributes: { faq_name: 'all_topics' },
  });

  const handleAllTopicsPress = () => {
    handleSectionDividerPressAnalytics();
    if (isWeb && subTitleUrl) {
      void Linking.openURL(subTitleUrl);
    } else {
      router.push('/myvaro/help/topics');
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={() => navigateBack()}
            />
          ),
        }}
      />
      <ScrollView
        style={styles.scrollViewContainer}
        refreshControl={
          <RefreshControl
            refreshing={loadingContactUsTileData || loadingHelpAndSupportData}
            onRefresh={refetch}
          />
        }
      >
        {warningMessage ? (
          <SystemBanner
            bannerType={SystemBannerType.INFO}
            titleText={warningMessage}
            onDismiss={() => {}}
          />
        ) : null}
        <View style={styles.topContainer}>
          {chatTileInfo ? (
            <>
              <View style={styles.messageContainer} testID="chatTileInfo">
                <TitleLgText numberOfLines={0}>
                  {chatTileInfo.title}
                </TitleLgText>
                <BodyBaseText numberOfLines={0}>
                  {chatTileInfo.message}
                </BodyBaseText>
                {chatTileInfo.isChatOnline && chatTileInfo.ctaCopy ? (
                  <Button
                    label={chatTileInfo.ctaCopy}
                    onPress={() =>
                      router.push({ pathname: `/${flow}/help/chat` })
                    }
                    analyticsId="chatWithUs"
                  />
                ) : null}
              </View>
              <View style={styles.hairlineDividerContainer}>
                <HairLineDivider />
              </View>
            </>
          ) : null}
          {contactUsTileInfo?.title ? (
            <ListItem
              primaryCopy={contactUsTileInfo.title}
              secondaryCopy={contactUsTileInfo.message}
              secondaryNumberOfLines={0}
              onPress={() => router.push({ pathname: `/${flow}/help/call-us` })}
              leadingAccessory={{
                id: 'icon',
                name: VXLComponentId.ICON,
                props: { iconKey: 'DISPUTE' },
              }}
              analyticsId="supportCall"
            />
          ) : null}
        </View>
        <View style={styles.pageDivider} />
        <View style={styles.bottomContainer}>
          <SectionDivider
            labelText={translatedText('topics.title')}
            trailingItemType="link"
            trailingItem={{
              linkText: subTitle || '',
              onButtonPress: handleAllTopicsPress,
            }}
          />
          <ListItemGroup items={formattedPopularTopicsListData} />
        </View>
      </ScrollView>
      <ScreenTransitionOverlay
        isLoading={loadingContactUsTileData || loadingHelpAndSupportData}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={error.message || ''}
              tryAgainAction={refetch}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bottomContainer: { marginTop: theme.spacing.s },
  container: { backgroundColor: theme.color.background.primary, flex: 1 },
  hairlineDividerContainer: { paddingHorizontal: theme.spacing.m },
  messageContainer: { gap: theme.spacing.m, padding: theme.spacing.m },
  pageDivider: { backgroundColor: theme.color.background.tertiary, height: 8 },
  scrollViewContainer: { flex: 1, gap: theme.spacing.s },
  topContainer: { backgroundColor: theme.color.background.primary },
});

export default Help;
